import { useEffect, useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { initTelegramWebApp } from './services/telegramService';
import Dashboard from './pages/Dashboard';
import LinkCopyTrade from './pages/LinkCopyTrade';
import Exchanges from './pages/Exchanges';
import Withdraw from './pages/Withdraw';
import History from './pages/History';
import CheckIn from './pages/CheckIn';
import Leaderboard from './pages/Leaderboard';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Layout from './components/Layout';
import SplashScreen from './components/SplashScreen';
import CustomModal from './components/CustomModal';
import { useModal } from './hooks/useModal';

function AppContent() {
  const { isAuthenticated, isLoading, autoLogin } = useAuthStore();
  const [showSplash, setShowSplash] = useState(true);
  const [appReady, setAppReady] = useState(false);
  const { modal, showWarning, showAlert, closeModal } = useModal();

  // Memoize the warning handler to avoid recreating on every render
  const handleCloseWarning = useCallback(async () => {
    const confirmed = await showWarning('Changes that you made may not be saved.');
    if (confirmed && typeof window !== 'undefined' && window.Telegram?.WebApp) {
      window.Telegram.WebApp.close();
    }
  }, [showWarning]);

  // Listen for custom alert events (fallback for non-Telegram browsers)
  useEffect(() => {
    const handleAlert = (e: any) => {
      showAlert(e.detail.message);
    };
    window.addEventListener('telegram-alert', handleAlert);
    return () => window.removeEventListener('telegram-alert', handleAlert);
  }, []);

  useEffect(() => {
    // Initialize Telegram Web App
    initTelegramWebApp();

    // Auto login with Telegram ID - always try to login
    if (isLoading) {
      autoLogin().finally(() => {
        setAppReady(true);
      });
    } else {
      setAppReady(true);
    }
  }, [isLoading, autoLogin]);

  useEffect(() => {
    // Handle Telegram BackButton (X button) - show warning before closing
    // Only setup when app is ready and authenticated
    if (!appReady || isLoading || !isAuthenticated) {
      return;
    }

    const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : null;
    
    if (!tg) {
      return;
    }

    // Enable closing confirmation to intercept close attempts
    // This will show Telegram's default confirmation, but we'll override with our custom modal
    if (tg.enableClosingConfirmation) {
      tg.enableClosingConfirmation();
    }

    if (!tg.BackButton) {
      return;
    }

    const handler = () => {
      // Show our custom warning modal instead of default Telegram confirmation
      handleCloseWarning();
    };
    
    // Always show BackButton so user can close app with warning
    // Note: BackButton will only be visible if there's history to go back to
    // But we can still set up the handler for when it becomes visible
    tg.BackButton.onClick(handler);
    
    // Try to show BackButton - it may not show if we're at root, but handler will work if it does
    try {
      tg.BackButton.show();
    } catch (error) {
      // BackButton might not be available to show in some cases
    }

    return () => {
      if (tg?.BackButton) {
        try {
          tg.BackButton.offClick(handler);
          tg.BackButton.hide();
        } catch (error) {
          // Ignore cleanup errors
        }
      }
      if (tg?.disableClosingConfirmation) {
        tg.disableClosingConfirmation();
      }
    };
  }, [appReady, isLoading, isAuthenticated, handleCloseWarning]);

  const handleSplashFinish = () => {
    setShowSplash(false);
  };

  if (showSplash || !appReady || isLoading) {
    return <SplashScreen onFinish={handleSplashFinish} />;
  }

  // If not authenticated after auto login, show error or retry
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0a0a0a] text-white p-4">
        <div className="text-center">
          <div className="text-xl font-bold mb-2">Unable to Login</div>
          <div className="text-sm text-tg-hint mb-4">
            Please open this app from Telegram
          </div>
          <button
            onClick={() => {
              window.location.reload();
            }}
            className="px-4 py-2 bg-primary text-black rounded-lg font-semibold"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<Navigate to="/overview" replace />} />
            <Route path="overview" element={<Dashboard />} />
            <Route path="link-copy-trade" element={<LinkCopyTrade />} />
            <Route path="exchanges" element={<Exchanges />} />
            <Route path="rebate" element={<Navigate to="/exchanges" replace />} />
            <Route path="withdraw" element={<Withdraw />} />
            <Route path="history" element={<History />} />
            <Route path="check-in" element={<CheckIn />} />
            <Route path="leaderboard" element={<Leaderboard />} />
            <Route path="profile" element={<Profile />} />
            <Route path="settings" element={<Settings />} />
          </Route>
          {/* Redirect all other routes to overview */}
          <Route path="*" element={<Navigate to="/overview" replace />} />
        </Routes>
      </Router>
      <CustomModal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText={modal.confirmText}
        cancelText={modal.cancelText}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
      />
    </>
  );
}

function App() {
  return <AppContent />;
}

export default App;
